import React, { useMemo } from "react";
import Button from "../Button/Button";
import ToggleSlider from "../ToggleSlider/ToggleSlider";
import Tooltip from "../Tooltip/Tooltip";
import { ImproveTemplateDto } from "../../../Types/Improve/ImproveTemplateDto";
import {
  ImproveExportColumnKey,
  ImproveExportFieldVisibility,
} from "../../../Utils/Export/ImproveExport/types";
import {
  IMPROVE_EXPORT_DISABLED_FIELD_TOOLTIP,
  getImproveExportColumnLabelMap,
  getImproveExportColumnToggleableMap,
  splitImproveExportColumnsByToggleable,
} from "../../../Utils/Export/ImproveExport/improveExportTemplateFields";
import wizardStyles from "../LandingPages/BulkExportWizard/BulkExportWizard.module.css";

export type ImproveExportColumnPickerProps = {
  fieldVisibility: ImproveExportFieldVisibility;
  templates: ImproveTemplateDto[];
  busy?: boolean;
  onColumnChange: (key: ImproveExportColumnKey, on: boolean) => void;
  onToggleSelectAll: () => void;
  allColumnsSelected: boolean;
};

const ImproveExportColumnPicker: React.FC<ImproveExportColumnPickerProps> = ({
  fieldVisibility,
  templates,
  busy = false,
  onColumnChange,
  onToggleSelectAll,
  allColumnsSelected,
}) => {
  const toggleableMap = useMemo(
    () => getImproveExportColumnToggleableMap(templates),
    [templates]
  );

  const labelMap = useMemo(
    () => getImproveExportColumnLabelMap(templates),
    [templates]
  );

  const { enabled: enabledColumns, disabled: disabledColumns } = useMemo(
    () => splitImproveExportColumnsByToggleable(toggleableMap),
    [toggleableMap]
  );

  const renderColumnRow = (key: ImproveExportColumnKey, toggleable: boolean) => {
    const row = (
      <div
        className={`${wizardStyles.toggleRow} ${
          toggleable ? "" : wizardStyles.toggleRowDisabled
        }`}
      >
        <span className={wizardStyles.toggleLabel}>{labelMap[key]}</span>
        <ToggleSlider
          isOn={toggleable ? fieldVisibility[key] : false}
          onToggle={(next) => onColumnChange(key, next)}
          disableConfirm
          size="small"
          disabled={!toggleable || busy}
        />
      </div>
    );

    if (toggleable) {
      return (
        <div key={key} className={wizardStyles.toggleListItem}>
          {row}
        </div>
      );
    }

    return (
      <Tooltip
        key={key}
        text={IMPROVE_EXPORT_DISABLED_FIELD_TOOLTIP}
        align="top"
        sideAlign="left"
      >
        <div className={wizardStyles.toggleListItem}>{row}</div>
      </Tooltip>
    );
  };

  return (
    <>
      <p className={wizardStyles.stepHint}>
        Choose which columns to include. Disabled items are omitted from the
        export.
      </p>
      <div className={wizardStyles.listToolbar}>
        <Button
          size="Small"
          colour="#FFF"
          textColour="#000"
          border="rgba(0,0,0,0.2)"
          disabled={busy}
          clickHandler={onToggleSelectAll}
        >
          {allColumnsSelected ? "Deselect all" : "Select all"}
        </Button>
      </div>
      <div className={wizardStyles.toggleList}>
        {enabledColumns.map((key) => renderColumnRow(key, true))}
        {disabledColumns.map((key) => renderColumnRow(key, false))}
      </div>
    </>
  );
};

export default ImproveExportColumnPicker;
